import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { NgRedux } from '@angular-redux3/store';
import { determineOriginFromRole } from '@ps20-frontend/shop-integration-redux';
import { Observable, Subscription } from 'rxjs';
import { Role } from '@ps20/bff-models';
import abbreviationsJson from './abbreviation.json';

export const TEXT_SERVICE_CONTENT = new InjectionToken('content');

const ABBREVIATIONS: Record<string, string> = abbreviationsJson;

function buildAbbreviationRegex(): RegExp {
  const escapedAbbr: Array<string> = [];
  for (const short of Object.keys(ABBREVIATIONS)) {
    escapedAbbr.push(escapeRegExpStatic(short));
    const shortCapitalized = short.charAt(0).toUpperCase() + short.slice(1);
    if (!shortCapitalized.startsWith(short.charAt(0))) {
      escapedAbbr.push(escapeRegExpStatic(shortCapitalized));
    }
    if (short.includes(' ')) {
      escapedAbbr.push(escapeRegExpStatic(short.replace(' ', '&nbsp;')));
      if (!shortCapitalized.startsWith(short.charAt(0))) {
        escapedAbbr.push(escapeRegExpStatic(shortCapitalized.replace(' ', '&nbsp;')));
      }
    }
  }
  return new RegExp(
    String.raw`(?:[ (>\t\n\r]|^)(${escapedAbbr.join('|')})(?=[ ,.():<\t\n\r]|$)`,
    'g',
  );
}

function escapeRegExpStatic(text: string): string {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, String.raw`\$&`);
}

const ABBREVIATION_REGEX: RegExp = buildAbbreviationRegex();

const ABBREVIATION_CACHE_LIMIT = 512;
const ABBREVIATION_CACHE = new Map<string, string>();

@Injectable({
  providedIn: 'root',
})
export class TextService implements OnDestroy {
  public role$: Observable<Role>;
  public isSdwSession$: Observable<boolean>;
  private contentKey: string;
  private readonly roleSubscription$: Subscription;
  private readonly isSdwSessionSubscription$: Subscription;

  /* eslint-disable @angular-eslint/prefer-inject -- manually constructed with `new` in several pipe specs, bypassing Angular DI; inject() only works within an active injection context and would throw NG0203 there */
  constructor(
    @Inject(TEXT_SERVICE_CONTENT) private readonly content: Record<string, unknown> | string,
    private readonly ngRedux: NgRedux<{ transientState: { role: Role } }>,
  ) {
    /* eslint-enable @angular-eslint/prefer-inject */
    this.role$ = this.ngRedux.select(['transientState', 'role']);
    this.isSdwSession$ = this.ngRedux.select(['transientState', 'isSdwSession']);
    let isSdwSession = false;
    if (this.isSdwSession$) {
      this.isSdwSessionSubscription$ = this.isSdwSession$.subscribe(value => {
        isSdwSession = value;
      });
    }
    if (this.role$) {
      this.roleSubscription$ = this.role$.subscribe(newRoleFromState => {
        this.setContentKey(determineOriginFromRole(newRoleFromState, isSdwSession));
      });
    }
  }

  ngOnDestroy(): void {
    this.roleSubscription$.unsubscribe();
  }

  /**
   * Allows to configure the TextService to use the specified content subtree.
   *
   * ´
   * @example
   * {
   *     key1: {text-content-key: value}
   *     ^^^=contentKey
   *     key2: {...}
   * }
   * @param contentKey
   */
  setContentKey(contentKey: string): void {
    this.contentKey = contentKey;
  }

  /**
   * Returns text content for given key.
   * @param textKey key in path format separated with dots, e.g. 'block.element.label'
   * @param returnDefaultKey specifies whether the contentKey gets returned if no text found
   * @param addAbbreviation specifies whether addAbbreviationTagToText() will be called before returning the result
   * @param params specifies parameter-replacements; format: {a: 'an', b: '(for example)'} variables in text format: 'this is {{a}} example text {{b}}.'
   */
  get(
    textKey: string,
    returnDefaultKey = true,
    addAbbreviation = true,
    params: { [key: string]: string } = {},
  ): string {
    let result = this.internalGet(textKey, this.contentKey, false);
    if (!result) {
      result = this.internalGet(textKey, (this.content as Record<string, unknown>)['default'] as string, returnDefaultKey);
    }
    result = this.replaceParams(result, params);
    return addAbbreviation ? this.addAbbreviationTagToText(result) : result;
  }

  replaceParams(text: string, params: { [key: string]: string }): string {
    if (params) {
      const objectKeys = Object.getOwnPropertyNames(params);
      if (Array.isArray(objectKeys) && objectKeys.length > 0) {
        objectKeys.forEach(key => {
          const textPart = new RegExp(`{{${key}}}`, 'gi');
          text = text.replace(textPart, params[key]);
        });
      }
    }
    return text;
  }

  getScreenreader(textKey: string): string {
    return this.internalGet(textKey, 'SCREENREADER', false);
  }

  private internalGet(textKey: string, contentKey: string, returnKeyAsDefault: boolean): string {
    if (!textKey) {
      textKey = '';
    }
    const text = textKey.split('.').reduce(function (object, key) {
      return object && (object as Record<string, unknown>)[key];
    }, (this.content as Record<string, unknown>)[contentKey] as unknown);
    if (!text) {
      return returnKeyAsDefault ? textKey : '';
    } else {
      return text as string;
    }
  }
  replaceUmlautsLowerCase(text: string): string {
    return text?.toLocaleLowerCase().replace('ae', 'ä').replace('ue', 'ü').replace('oe', 'ö');
  }

  addAbbreviationTagToText(text: string): string {
    if (typeof text !== 'string' || text.length === 0) {
      return text;
    }
    const cached = ABBREVIATION_CACHE.get(text);
    if (cached !== undefined) {
      return cached;
    }
    ABBREVIATION_REGEX.lastIndex = 0;
    const result = text.replace(ABBREVIATION_REGEX, value => this.replacer(ABBREVIATIONS, value));
    if (ABBREVIATION_CACHE.size >= ABBREVIATION_CACHE_LIMIT) {
      ABBREVIATION_CACHE.clear();
    }
    ABBREVIATION_CACHE.set(text, result);
    return result;
  }

  private replacer(abbreviations: Record<string, string>, value: string): string {
    const regEx = /^([^A-z])/;
    const unrelatedMatch: RegExpExecArray | null = regEx.exec(value);
    const unrelatedMatchString: string = unrelatedMatch ? unrelatedMatch[0] : '';
    value = value.replace(regEx, '');
    let valueWithSpace = value.replace('&nbsp;', ' ');
    if (!abbreviations[valueWithSpace]) {
      valueWithSpace = valueWithSpace.charAt(0).toLowerCase() + valueWithSpace.slice(1);
    }
    return abbreviations[valueWithSpace]
      ? `${unrelatedMatchString}<abbr title="${abbreviations[valueWithSpace]}">${value}</abbr>`
      : value;
  }
}
